import clsx from 'clsx';
import React, { memo, useCallback } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';

import { TableViews } from './constants';

interface TableFilterButtonsProps {
  currentView: TableViews;
  setCurrentView: (arg0: TableViews) => void;
}

const NET_SENTIMENT = 'Net Sentiment';
const OPTIMISTIC = 'Optimistic (%)';
const PESSIMISTIC = 'Pessimistic (%)';

const SharedButtonStyles =
  'h-48 max-w-[173px] w-full rounded-28 border border-solid bg-white text-body-md md:text-subheadline-sm font-suisse-screen font-medium ease-in-out duration-200 touch-manipulation border-grayscale-gray';

const TableFilterButtons = ({
  currentView,
  setCurrentView,
}: TableFilterButtonsProps): JSX.Element => {
  const { trackEvent } = useEventTracking();

  const changeView = useCallback(
    (newView: TableViews) => {
      setCurrentView(newView);
      trackEvent(click({ clickTarget: ClickTarget.SwitchTable, clickText: newView }));
    },
    [setCurrentView, trackEvent]
  );

  return (
    <div className="relative mx-auto flex max-w-full flex-col items-center justify-center gap-12 px-16 md:w-[1072px] md:flex-row">
      <span className="shrink-0 font-suisse-screen text-subheadline-sm !font-bold md:text-subheadline-md">
        Click to view:{' '}
      </span>
      <div className="flex w-full flex-col items-center justify-center gap-12 px-16 xs:flex-row md:justify-start md:px-0">
        <button
          className={clsx(
            'sm:hover:border-grayscale-beige sm:hover:bg-grayscale-beige',
            SharedButtonStyles,
            {
              'border-4 !border-grayscale-beige': currentView === TableViews.DIFFERENCE,
            }
          )}
          type="button"
          onClick={() => changeView(TableViews.DIFFERENCE)}
          aria-label="Switch view to Net Sentiment table"
        >
          {NET_SENTIMENT}
        </button>

        <button
          className={clsx(
            'sm:hover:border-secondary-green-light sm:hover:bg-secondary-green-light',
            SharedButtonStyles,
            {
              'border-4 !border-secondary-green-light': currentView === TableViews.OPTIMISTIC,
            }
          )}
          type="button"
          onClick={() => changeView(TableViews.OPTIMISTIC)}
          aria-label="Switch view to Optimistic outlook table"
        >
          {OPTIMISTIC}
        </button>

        <button
          className={clsx(
            'sm:hover:border-primary-pink sm:hover:bg-primary-pink',
            SharedButtonStyles,
            {
              'border-4 !border-primary-pink': currentView === TableViews.PESSIMISTIC,
            }
          )}
          type="button"
          onClick={() => changeView(TableViews.PESSIMISTIC)}
          aria-label="Switch view to Pessimistic outlook table"
        >
          {PESSIMISTIC}
        </button>
      </div>
    </div>
  );
};

export default memo(TableFilterButtons);
